import * as React from 'react';

import { mergeCallbacks, usePopoverContext_unstable } from '@fluentui/react-components';

import {
  Preview,
  PreviewContent,
  PreviewHeader,
  PreviewMetadata,
  PreviewSurface,
  PreviewTrigger,
} from '@fluentui-copilot/react-preview';
import { SensitivityLabel } from '@fluentui-copilot/react-sensitivity-label';
import type {
  PreviewHeaderProps,
  PreviewMetadataProps,
  PreviewProps,
  PreviewSurfaceProps,
} from '@fluentui-copilot/react-preview';
import type { SensitivityLabelProps } from '@fluentui-copilot/react-sensitivity-label';

import { Citation } from '../Citation';
import { Reference } from '../Reference';
import type { CitationProps } from '../Citation';
import type { ReferenceProps } from '../Reference';

export type PackagedCitation = React.FC<CitationProps>;

export type PackagedReference = React.FC<ReferenceProps>;

export type PackagedPreviewContentProps = Pick<
  PreviewMetadataProps,
  'icon' | 'primaryText' | 'secondaryText' | 'tertiaryText'
> & {
  content?: React.ReactNode;
};

export type ReferenceCitationPreviewGeneratorProps = {
  citationPreviewProps?: Omit<PreviewProps, 'children'> & Partial<Pick<PreviewProps, 'children'>>;
  citationPreviewSurfaceProps?: PreviewSurfaceProps;
  citationProps?: CitationProps;
  index: number;
  previewContentProps?: PackagedPreviewContentProps;
  previewHeaderProps?: PreviewHeaderProps;
  referencePreviewProps?: Omit<PreviewProps, 'children'> & Partial<Pick<PreviewProps, 'children'>>;
  referencePreviewSurfaceProps?: PreviewSurfaceProps;
  referenceProps?: ReferenceProps;
  sensitivityLabelProps?: SensitivityLabelProps;
};

export type ReferenceCitationPreviewGenerator = (props: ReferenceCitationPreviewGeneratorProps) => {
  Citation: PackagedCitation;
  Reference: PackagedReference;
};

// Abstract the citation that serves as the close button of the Preview into its own component so that we have access to the popover context.
const CitationCloseButton = (citationCloseButtonProps: CitationProps) => {
  const setPopoverOpen = usePopoverContext_unstable(context => context.setOpen);

  const onPopoverCloseClick = React.useCallback<NonNullable<CitationProps['onClick']>>(
    ev => {
      setPopoverOpen(ev, false);

      // Don't navigate
      ev.preventDefault();
    },
    [setPopoverOpen],
  );

  const onCitationCloseButtonClick = mergeCallbacks(onPopoverCloseClick, citationCloseButtonProps.onClick);

  return <Citation {...citationCloseButtonProps} onClick={onCitationCloseButtonClick} />;
};

/**
 * Function to generate a Citation - Reference pair with correctly wired props.
 * Both the Citation and the Reference act as trigger components to Previews that are also correctly wired by the function.
 * Props can either be passed in to the function or applied directly to the generated components in JSX.
 *
 * A `useReferenceCitationPreview` hook exists that calls this function and memoizes its return value based on the props.
 * You should use the hook when the props used to generate the Citation - Reference pair can change dynamically.
 * If they are static, or in case you wanted to generate multiple pairs within a cycle, you should call
 * `generateReferenceCitationPreview` directly.
 *
 * @returns A Citation - Reference pair with correctly wired props.
 */
export const generateReferenceCitationPreview: ReferenceCitationPreviewGenerator = props => {
  const {
    citationPreviewProps,
    citationPreviewSurfaceProps,
    citationProps,
    index,
    previewContentProps,
    previewHeaderProps,
    referencePreviewProps,
    referencePreviewSurfaceProps,
    referenceProps,
    sensitivityLabelProps,
  } = props;

  const CitationWithPreview: PackagedCitation = props => {
    return (
      <Preview closeOnScroll positioning="above" size="small" trapFocus withArrow {...citationPreviewProps}>
        <PreviewTrigger>
          <Citation {...citationProps} {...props}>
            {index}
          </Citation>
        </PreviewTrigger>
        <PreviewSurface tabIndex={0} {...citationPreviewSurfaceProps}>
          <PreviewContent>
            <PreviewHeader
              citation={
                <CitationCloseButton aria-label={`Close Reference ${index}`} referenceHref="#" tabIndex={-1}>
                  {index}
                </CitationCloseButton>
              }
              media={referenceProps?.graphic as PreviewHeaderProps['media']}
              {...previewHeaderProps}
            >
              {referenceProps?.children}
            </PreviewHeader>
            <PreviewMetadata
              icon={previewContentProps?.icon}
              primaryText={previewContentProps?.primaryText}
              secondaryText={previewContentProps?.secondaryText}
              tertiaryText={previewContentProps?.tertiaryText}
            />
            {sensitivityLabelProps && <SensitivityLabel {...sensitivityLabelProps} />}
            {previewContentProps?.content ?? citationPreviewProps?.children}
          </PreviewContent>
        </PreviewSurface>
      </Preview>
    );
  };

  const ReferenceWithPreview: PackagedReference = props => {
    const mergedReferenceProps = {
      citation: index,
      ...referenceProps,
      ...props,
    } as ReferenceProps;

    return (
      <Preview closeOnScroll positioning="above" size="small" trapFocus withArrow {...referencePreviewProps}>
        <PreviewTrigger>
          <Reference {...mergedReferenceProps} />
        </PreviewTrigger>
        <PreviewSurface tabIndex={0} {...referencePreviewSurfaceProps}>
          <PreviewContent>
            <PreviewHeader
              citation={
                <CitationCloseButton aria-label={`Close Reference ${index}`} referenceHref="#" tabIndex={-1}>
                  {index}
                </CitationCloseButton>
              }
              media={referenceProps?.graphic as PreviewHeaderProps['media']}
              {...previewHeaderProps}
            >
              {previewHeaderProps?.children ?? referenceProps?.children}
            </PreviewHeader>
            <PreviewMetadata
              icon={previewContentProps?.icon}
              primaryText={previewContentProps?.primaryText}
              secondaryText={previewContentProps?.secondaryText}
              tertiaryText={previewContentProps?.tertiaryText}
            />
            {sensitivityLabelProps && <SensitivityLabel {...sensitivityLabelProps} />}
            {previewContentProps?.content ?? referencePreviewProps?.children}
          </PreviewContent>
        </PreviewSurface>
      </Preview>
    );
  };

  const components = {
    Citation: CitationWithPreview,
    Reference: ReferenceWithPreview,
  };

  return components;
};
